/**
 * Create Success Story Page - Admin only
 * @file src/web/features/achievements/pages/admin/create.tsx
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { useSuccessStories } from '../../hooks/useSuccessStories';
import { SEO } from '../../../../shared/components';
import {
  Plus,
  Upload,
  X,
  User,
  Loader2,
  Award
} from 'lucide-react';

const CreateSuccessStoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { createStory, uploadImage } = useSuccessStories(true);

  const [name, setName] = useState('');
  const [board, setBoard] = useState('CBSE Board');
  const [score, setScore] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [quote, setQuote] = useState('');
  const [currentSchool, setCurrentSchool] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !board.trim() || !score.trim()) {
      setError('Name, board, and score are required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create story first
      const story = await createStory({
        name: name.trim(),
        board: board.trim(),
        score: score.trim(),
        year,
        quote: quote.trim() || undefined,
        currentSchool: currentSchool.trim() || undefined,
      });

      // Upload image if provided
      if (imageFile) {
        await uploadImage(story.id, imageFile);
      }

      navigate('/achievements/admin');
    } catch (err: any) {
      setError(err.message || 'Failed to create success story');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Add Success Story - Admin Panel" />
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <Plus className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-white">
                  Add Success Story
                </h1>
                <p className="mt-1 text-amber-100 text-sm">
                  Add a new student success story and achievement
                </p>
              </div>
            </div>
          </div>

          {/* Form Card */}
          <Card className="border-t-4 border-t-amber-500 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Award className="h-5 w-5 text-amber-600" />
                </div>
                Story Details
              </CardTitle>
              <CardDescription className="mt-1.5">
                Fill in the student's achievement details
              </CardDescription>
            </div>

            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                {/* Student Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Photo
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-10 h-10 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="image-upload"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Photo
                      </label>
                      {imageFile && (
                        <button
                          type="button"
                          onClick={removeImage}
                          className="ml-2 text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Name and Board */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Name *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Lalenthika"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board/Curriculum *
                    </label>
                    <select
                      value={board}
                      onChange={(e) => setBoard(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    >
                      <option value="CBSE Board">CBSE Board</option>
                      <option value="IGCSE">IGCSE</option>
                      <option value="IB">IB Diploma</option>
                      <option value="State Board">State Board</option>
                      <option value="Entrance Exam">Entrance Exam</option>
                    </select>
                  </div>
                </div>

                {/* Score and Year */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Score/Grade *
                    </label>
                    <input
                      type="text"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      placeholder="e.g., 93.4% or A*"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year
                    </label>
                    <input
                      type="number"
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value))}
                      min={2020}
                      max={2030}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Current School */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current School/Status
                  </label>
                  <input
                    type="text"
                    value={currentSchool}
                    onChange={(e) => setCurrentSchool(e.target.value)}
                    placeholder="e.g., Currently pursuing Science at DPS Modern Indian School, Qatar"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Quote/Testimonial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Quote/Testimonial
                  </label>
                  <textarea
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    placeholder="Student's testimonial about their experience at Tuition Point..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/achievements/admin')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Story
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CreateSuccessStoryPage;
